// packages/console/src/lib/format.ts
//
// Display helpers carrying the panel-map conventions. The absent-value glyph
// (R1) is the em dash U+2014, produced here from a Unicode escape so no literal
// em dash appears in authored source (convention C4). Every consumer renders
// EM_DASH for a value that does not currently exist, never a fabricated zero.

// U+2014 em dash, built from an escape so no literal dash appears in source.
export const EM_DASH = "\u2014";

// fmtDur formats seconds as HH:MM:SS (zero-padded), matching the prototype.
export function fmtDur(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const hh = String(Math.floor(s / 3600)).padStart(2, "0");
  const mm = String(Math.floor(s / 60) % 60).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
}

// fmtEta formats a positive seconds figure into a compact h/m/s label, or the
// em dash when the figure is absent (null) or non-positive-and-unknown.
export function fmtEta(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds) || seconds < 0) return EM_DASH;
  if (seconds >= 3600) return `${Math.floor(seconds / 3600)}h ${Math.floor(seconds / 60) % 60}m`;
  if (seconds >= 60) return `${Math.floor(seconds / 60)}m ${Math.floor(seconds % 60)}s`;
  return `${Math.max(0, Math.floor(seconds))}s`;
}

const MIB = 1024 * 1024;
const GIB = 1024 * MIB;

// bytesToGB converts a byte count to GiB with one decimal.
export function gib(bytes: number): number {
  return bytes / GIB;
}

// fmtGB renders a byte count as a size string with 2 decimals by default.
// Under 1 GiB it renders in MB (also 2 decimals) so small files read
// accurately (e.g. 350 MB -> "348.62 MB", not "0.34 GB").
export function fmtGB(bytes: number, digits = 2): string {
  if (bytes < GIB) return `${(bytes / MIB).toFixed(digits)} MB`;
  return `${gib(bytes).toFixed(digits)} GB`;
}

// fmtInt renders an integer with thousands separators, or the em dash for a
// null (absent) value.
export function fmtInt(v: number | null): string {
  if (v === null || !Number.isFinite(v)) return EM_DASH;
  return Math.round(v).toLocaleString();
}

// fmtNum renders a fixed-decimal number, or the em dash for a null value.
export function fmtNum(v: number | null, digits = 1): string {
  if (v === null || !Number.isFinite(v)) return EM_DASH;
  return v.toFixed(digits);
}

// fmtPct renders a whole-percent, or the em dash for a null value.
export function fmtPct(v: number | null): string {
  if (v === null || !Number.isFinite(v)) return EM_DASH;
  return `${Math.round(v)}%`;
}

// mbps converts a bits/sec figure to Mb/s (megabits, base-10 as media rates are
// quoted), returning null for a null/non-finite input so the caller renders an
// em-dash. This is the numeric conversion; fmtMbps is the display form.
export function mbps(bitsPerSec: number | null): number | null {
  if (bitsPerSec === null || !Number.isFinite(bitsPerSec)) return null;
  return bitsPerSec / 1_000_000;
}

// fmtMbps renders a bits/sec figure as a fixed-decimal Mb/s string, or the em
// dash for an absent value (honest absence, never a fabricated 0).
export function fmtMbps(bitsPerSec: number | null, digits = 2): string {
  const v = mbps(bitsPerSec);
  if (v === null) return EM_DASH;
  return v.toFixed(digits);
}

// fmtClock renders a seconds figure as a media-clock string: mm:ss for short
// files and h:mm:ss once it reaches an hour, so an axis tick or a hover readout
// reads naturally at any duration. Negative or non-finite input renders 0:00.
export function fmtClock(totalSeconds: number): string {
  const s = Number.isFinite(totalSeconds) ? Math.max(0, Math.floor(totalSeconds)) : 0;
  const h = Math.floor(s / 3600);
  const m = Math.floor(s / 60) % 60;
  const ss = String(s % 60).padStart(2, "0");
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${ss}`;
  return `${m}:${ss}`;
}

// fmtTimeOfDay renders an RFC3339 timestamp as HH:MM:SS local time. Falls back
// to the em dash if the timestamp does not parse.
export function fmtTimeOfDay(rfc3339: string): string {
  const d = new Date(rfc3339);
  if (Number.isNaN(d.getTime())) return EM_DASH;
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}
